'use strict';
/**
 * Built-in default translations for the avatar's UI labels, keyed by language
 * code (matching the plugin's SupportedLanguage values; `en` is the fallback).
 * Resolved from the plugin `language` setting and overridable per-label via
 * `avatarConfig.labels`.
 */
const EN = {
    ariaLabel: 'Assistant avatar',
    settings: 'Avatar settings',
    subtitles: 'Subtitles',
    chat: 'Chat',
    voiceInput: 'Speak to the assistant',
    textInput: 'Type to the assistant',
    listening: 'Listening',
    toggleConversation: 'Show or hide the conversation',
    send: 'Send',
    messagePlaceholder: 'Type a message…',
    loadError: 'Could not load the avatar.',
    connectError: 'Could not connect to the avatar.'
};
const TRANSLATIONS = {
    en: EN,
    de: {
        ariaLabel: 'Assistenten-Avatar',
        settings: 'Avatar-Einstellungen',
        subtitles: 'Untertitel',
        chat: 'Chat',
        voiceInput: 'Mit dem Assistenten sprechen',
        textInput: 'Dem Assistenten schreiben',
        listening: 'Ich höre zu',
        toggleConversation: 'Unterhaltung ein- oder ausblenden',
        send: 'Senden',
        messagePlaceholder: 'Nachricht eingeben…',
        loadError: 'Avatar konnte nicht geladen werden.',
        connectError: 'Verbindung zum Avatar fehlgeschlagen.'
    },
    fr: {
        ariaLabel: 'Avatar de l’assistant',
        settings: 'Paramètres de l’avatar',
        subtitles: 'Sous-titres',
        chat: 'Chat',
        voiceInput: 'Parler à l’assistant',
        textInput: 'Écrire à l’assistant',
        listening: 'À l’écoute',
        toggleConversation: 'Afficher ou masquer la conversation',
        send: 'Envoyer',
        messagePlaceholder: 'Saisissez un message…',
        loadError: 'Impossible de charger l’avatar.',
        connectError: 'Impossible de se connecter à l’avatar.'
    },
    nl: {
        ariaLabel: 'Assistent-avatar',
        settings: 'Avatarinstellingen',
        subtitles: 'Ondertiteling',
        chat: 'Chat',
        voiceInput: 'Met de assistent praten',
        textInput: 'Naar de assistent typen',
        listening: 'Ik luister',
        toggleConversation: 'Gesprek tonen of verbergen',
        send: 'Verzenden',
        messagePlaceholder: 'Typ een bericht…',
        loadError: 'Kan de avatar niet laden.',
        connectError: 'Kan geen verbinding maken met de avatar.'
    },
    tr: {
        ariaLabel: 'Asistan avatarı',
        settings: 'Avatar ayarları',
        subtitles: 'Altyazılar',
        chat: 'Sohbet',
        voiceInput: 'Asistanla konuşun',
        textInput: 'Asistana yazın',
        listening: 'Dinliyorum',
        toggleConversation: 'Sohbeti göster veya gizle',
        send: 'Gönder',
        messagePlaceholder: 'Bir mesaj yazın…',
        loadError: 'Avatar yüklenemedi.',
        connectError: 'Avatara bağlanılamadı.'
    },
    it: {
        ariaLabel: 'Avatar dell’assistente',
        settings: 'Impostazioni avatar',
        subtitles: 'Sottotitoli',
        chat: 'Chat',
        voiceInput: 'Parla con l’assistente',
        textInput: 'Scrivi all’assistente',
        listening: 'In ascolto',
        toggleConversation: 'Mostra o nascondi la conversazione',
        send: 'Invia',
        messagePlaceholder: 'Scrivi un messaggio…',
        loadError: 'Impossibile caricare l’avatar.',
        connectError: 'Impossibile connettersi all’avatar.'
    },
    pl: {
        ariaLabel: 'Awatar asystenta',
        settings: 'Ustawienia awatara',
        subtitles: 'Napisy',
        chat: 'Czat',
        voiceInput: 'Mów do asystenta',
        textInput: 'Pisz do asystenta',
        listening: 'Słucham',
        toggleConversation: 'Pokaż lub ukryj rozmowę',
        send: 'Wyślij',
        messagePlaceholder: 'Wpisz wiadomość…',
        loadError: 'Nie można załadować awatara.',
        connectError: 'Nie można połączyć się z awatarem.'
    },
    es: {
        ariaLabel: 'Avatar del asistente',
        settings: 'Configuración del avatar',
        subtitles: 'Subtítulos',
        chat: 'Chat',
        voiceInput: 'Hablar con el asistente',
        textInput: 'Escribir al asistente',
        listening: 'Escuchando',
        toggleConversation: 'Mostrar u ocultar la conversación',
        send: 'Enviar',
        messagePlaceholder: 'Escribe un mensaje…',
        loadError: 'No se pudo cargar el avatar.',
        connectError: 'No se pudo conectar con el avatar.'
    },
    'es-mx': {
        ariaLabel: 'Avatar del asistente',
        settings: 'Configuración del avatar',
        subtitles: 'Subtítulos',
        chat: 'Chat',
        voiceInput: 'Hablar con el asistente',
        textInput: 'Escribir al asistente',
        listening: 'Escuchando',
        toggleConversation: 'Mostrar u ocultar la conversación',
        send: 'Enviar',
        messagePlaceholder: 'Escribe un mensaje…',
        loadError: 'No se pudo cargar el avatar.',
        connectError: 'No se pudo conectar con el avatar.'
    },
    pt: {
        ariaLabel: 'Avatar do assistente',
        settings: 'Configurações do avatar',
        subtitles: 'Legendas',
        chat: 'Chat',
        voiceInput: 'Falar com o assistente',
        textInput: 'Escrever para o assistente',
        listening: 'A ouvir',
        toggleConversation: 'Mostrar ou ocultar a conversa',
        send: 'Enviar',
        messagePlaceholder: 'Escreva uma mensagem…',
        loadError: 'Não foi possível carregar o avatar.',
        connectError: 'Não foi possível conectar ao avatar.'
    }
};
/**
 * Resolve the avatar UI labels for a language, applying per-label overrides.
 * Falls back from an exact match (e.g. `es-mx`) to the base language (`es`)
 * and finally to English.
 *
 * @param language plugin language code (e.g. `'de'`, `'es-mx'`), case-insensitive
 * @param overrides per-label overrides from `avatarConfig.labels`
 */
export function resolveLabels(language, overrides) {
    const lang = (language || 'en').toString().trim().toLowerCase();
    const base = TRANSLATIONS[lang] || TRANSLATIONS[lang.split('-')[0]] || EN;
    return Object.assign(Object.assign({}, base), (overrides || {}));
}
